import posthog from 'posthog-js'
import { IS_POSTHOG_ENABLED } from '@/constants/analytics'
import useLocalStorage from './useLocalStorage'
import useStore from './store'
import useText from './text'
import type { LocationQuery } from 'vue-router'

export default function useEvent() {
  const { isSupportConnection, user } = useLocalStorage()
  const { objectiveSlug } = useStore()

  const { splitCamelCaseString } = useText()

  const send = (_: 'segment', eventName: string, data?: object) => {
    if (process.env.NODE_ENV === 'development' || isSupportConnection.value) {
      const tracking = {
        eventName: eventName
      }
      for (const key in data) {
        tracking[key] = data[key]
      }

      console.table({ segment: tracking })

      return
    }

    const fulldData = {
      ...data,
      os: getOsVersion()
    }
    return analytics.track(eventName, fulldData)
  }

  const getOsVersion = () => {
    const unknown = '-'

    const nVer = navigator.appVersion
    const nAgt = navigator.userAgent

    let os = unknown
    const clientStrings = [
      { s: 'Windows 10', r: /(Windows 10.0|Windows NT 10.0)/ },
      { s: 'Windows 8.1', r: /(Windows 8.1|Windows NT 6.3)/ },
      { s: 'Windows 8', r: /(Windows 8|Windows NT 6.2)/ },
      { s: 'Windows 7', r: /(Windows 7|Windows NT 6.1)/ },
      { s: 'Windows Vista', r: /Windows NT 6.0/ },
      { s: 'Windows Server 2003', r: /Windows NT 5.2/ },
      { s: 'Windows XP', r: /(Windows NT 5.1|Windows XP)/ },
      { s: 'Windows 2000', r: /(Windows NT 5.0|Windows 2000)/ },
      { s: 'Windows ME', r: /(Win 9x 4.90|Windows ME)/ },
      { s: 'Windows 98', r: /(Windows 98|Win98)/ },
      { s: 'Windows 95', r: /(Windows 95|Win95|Windows_95)/ },
      { s: 'Windows NT 4.0', r: /(Windows NT 4.0|WinNT4.0|WinNT|Windows NT)/ },
      { s: 'Windows CE', r: /Windows CE/ },
      { s: 'Windows 3.11', r: /Win16/ },
      { s: 'Android', r: /Android/ },
      { s: 'Open BSD', r: /OpenBSD/ },
      { s: 'Sun OS', r: /SunOS/ },
      { s: 'Linux', r: /(Linux|X11)/ },
      { s: 'iOS', r: /(iPhone|iPad|iPod)/ },
      { s: 'Mac OS X', r: /Mac OS X/ },
      { s: 'Mac OS', r: /(MacPPC|MacIntel|Mac_PowerPC|Macintosh)/ },
      { s: 'QNX', r: /QNX/ },
      { s: 'UNIX', r: /UNIX/ },
      { s: 'BeOS', r: /BeOS/ },
      { s: 'OS/2', r: /OS\/2/ },
      { s: 'Search Bot', r: /(nuhk|Googlebot|Yammybot|Openbot|Slurp|MSNBot|Ask Jeeves\/Teoma|ia_archiver)/ }
    ]
    for (const id in clientStrings) {
      const cs = clientStrings[id]
      if (cs.r.test(nAgt)) {
        os = cs.s
        break
      }
    }

    let osVersion

    if (/Windows/.test(os)) {
      osVersion = /Windows (.*)/.exec(os)[1]
      os = 'Windows'
    }

    switch (os) {
      case 'Mac OS X':
        osVersion = /Mac OS X (10[._\d]+)/.exec(nAgt)[1]
        break

      case 'Android':
        osVersion = /Android ([._\d]+)/.exec(nAgt)[1]
        break

      case 'iOS':
        osVersion = /OS (\d+)_(\d+)_?(\d+)?/.exec(nVer)
        osVersion = `${osVersion[1]}.${osVersion[2]}.${osVersion[3] | 0}`
        break
    }

    return {
      name: os,
      version: osVersion?.replaceAll('_', '.') || 'unknown version'
    }
  }

  const identifyUser = () => {
    if (isSupportConnection.value) {
      return
    }
    analytics.identify(user.value.publicId, {
      pongoId: user.value.id,
      role: user.value.roleTemplate.slug,
      name: user.value.name,
      firstName: user.value.firstname,
      lastName: user.value.lastname,
      email: user.value.email,
      phone: user.value.phone,
      jobTitle: user.value?.jobTitle,
      isSelfServe: user.value.merchant.isSelfServe,
      address: {
        country: user.value.language.iso639_1
      },
      company: {
        id: user.value.merchant.publicId,
        name: user.value.merchant.name
      },
      objective: user.value.merchant.isSelfServe ? objectiveSlug.value : null,
      createdAt: user.value.createdAt,
      os: getOsVersion()
    })

    if (IS_POSTHOG_ENABLED && user.value.publicId) {
      posthog.identify(user.value.publicId, {
        pongoId: user.value.id,
        email: user.value.email,
        name: user.value.name,
        firstName: user.value.firstname,
        lastName: user.value.lastname,
        role: user.value.roleTemplate.slug,
        jobTitle: user.value?.jobTitle,
        isSelfServe: user.value.merchant.isSelfServe,
        merchantId: user.value.merchant.publicId,
        merchantName: user.value.merchant.name,
        country: user.value.language.iso639_1,
        createdAt: user.value.createdAt
      })
      if (user.value.merchant.publicId) {
        posthog.group('merchant', user.value.merchant.publicId, {
          name: user.value.merchant.name,
          isSelfServe: user.value.merchant.isSelfServe
        })
      }
    }
  }

  const formatPageName = (routeName: string, query?: LocationQuery) => {
    let formattedRouteName = splitCamelCaseString(routeName)

    if (routeName === 'SignUp') {
      formattedRouteName = `${formattedRouteName} step ${query?.step ?? 1}`
    }

    return `${formattedRouteName} Page view`
  }

  const pageView = (routeName: string, query?: LocationQuery, data?: LocationQuery) => {
    if (isSupportConnection.value || routeName === 'Autologin') {
      return
    }
    identifyUser()
    const pageName = formatPageName(routeName, query)
    analytics.page(pageName, {
      os: getOsVersion(),
      data
    })
  }

  return {
    send,
    identifyUser,
    pageView,
    getOsVersion
  }
}
